import { track } from "../../lib/tracking";
import { Reveal } from "../Reveal";
import { useLang } from "../../context/LanguageContext";
import { Check, ArrowUpRight, MonitorPlay, ShieldCheck } from "lucide-react";

export function PartialPurchaseSection({ course }) {
  const { t } = useLang();
  if (!course.partial_enabled) return null;

  const onBuy = () => {
    track("click_acquista_parte", { course: course.title });
    if (course.partial_url) window.open(course.partial_url, "_blank", "noopener");
  };

  const features = (course.partial_features || []).filter(Boolean);

  return (
    <section data-testid="course-partial" className="bg-[#0B1220] py-20 md:py-28">
      <div className="px-6 md:px-12 max-w-[1400px] mx-auto">
        <Reveal className="grid grid-cols-1 lg:grid-cols-12 rounded-sm overflow-hidden border border-white/10">
          {/* Left: content */}
          <div className="lg:col-span-7 bg-[#11182B] p-8 md:p-12">
            <div className="flex items-center gap-2 text-[#5B9DFF]">
              <MonitorPlay className="h-5 w-5" strokeWidth={1.5} />
              <span className="text-xs font-semibold uppercase tracking-[0.22em]">{t("partial.badge")}</span>
            </div>
            <h2 className="font-heading text-3xl md:text-4xl font-light text-white mt-4">
              {course.partial_title || t("partial.fallback_title")}
            </h2>
            {course.partial_description && (
              <p className="mt-4 text-[#9AA7BD] font-light leading-relaxed max-w-xl">{course.partial_description}</p>
            )}
            {features.length > 0 && (
              <ul className="mt-7 grid sm:grid-cols-2 gap-x-8 gap-y-3">
                {features.map((f, i) => (
                  <li key={i} className="flex items-start gap-3 text-[#C9D2E3]">
                    <Check className="h-4 w-4 text-[#5B9DFF] mt-1 shrink-0" strokeWidth={2} />
                    <span className="text-sm">{f}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Right: price + CTA */}
          <div className="lg:col-span-5 relative isolate overflow-hidden bg-[#1452F0] p-8 md:p-12 flex flex-col justify-center glow-azure">
            <div className="absolute -top-16 -right-10 h-56 w-56 rounded-full bg-[#5B9DFF]/30 blur-3xl pointer-events-none" />
            <div className="relative">
              {course.partial_price && (
                <>
                  <span className="text-xs font-semibold uppercase tracking-[0.22em] text-white/80">{t("partial.price_label")}</span>
                  <div className="font-heading text-5xl text-white mt-1" data-testid="partial-price">{course.partial_price}</div>
                </>
              )}
              <button
                data-testid="partial-buy-button"
                onClick={onBuy}
                className="group w-full mt-8 bg-white text-[#0B1220] hover:bg-[#0B1220] hover:text-white transition-colors rounded-sm px-7 py-4 text-sm font-medium tracking-wide flex items-center justify-center gap-2"
              >
                {t("partial.buy")}
                <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </button>
              <div className="mt-4 flex items-center gap-2 text-white/70 text-xs">
                <ShieldCheck className="h-4 w-4" />
                {t("partial.secure")}
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
